/**
 * Tenant-level copy preset persistence.
 *
 * The *theme* declares what it was authored against (`pack.meta.copyPreset`).
 * The *tenant* declares what it permits at all (`tenantCopyPreset`). The
 * runtime applies the stricter of the two — see `intersectPresets()`.
 *
 * Phase 1 storage is localStorage keyed per-origin; Phase 2 will read the
 * tenant default from `tenant_settings` in Lovable Cloud without changing
 * this module's API.
 */
import {
  DEFAULT_COPY_PRESET_ID,
  isCopyPresetId,
  type CopyPresetId,
} from "@/theme/copy/presets";

const STORAGE_KEY = "smplify_tenant_copy_preset";

export function loadTenantCopyPreset(): CopyPresetId {
  if (typeof window === "undefined") return DEFAULT_COPY_PRESET_ID;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw && isCopyPresetId(raw)) return raw;
  } catch {
    // localStorage unavailable (private mode, SSR) — fall through.
  }
  return DEFAULT_COPY_PRESET_ID;
}

export function saveTenantCopyPreset(id: CopyPresetId): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // ignore quota / disabled storage
  }
}

export const TENANT_COPY_PRESET_STORAGE_KEY = STORAGE_KEY;
